import React, { useState, useEffect } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";

import ReportsFilters from "./ReportsFilters.js";

import { crossBase64, tickBase64 } from "../../../utils/images.js";
import { getReportsData } from "../../../APIS";

// Assign vfs safely
pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs;

const Reports = () => {
  const [totalData, setTotalData] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState("All");
  const itemsPerPage = 25;
  const [currentPage, setCurrentPage] = useState(0);
  const storedUser = sessionStorage.getItem("userDetails");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [filterDate, setFilterDate] = useState("");
  const [filterSubmittedBy, setFilterSubmittedBy] = useState("All");

  let token = null;
  if (storedUser) {
    const userDetails = JSON.parse(storedUser);
    token = userDetails?.token;
  }
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getReportsData(token);
        setTotalData(data);
        if (!data?.forms || !Array.isArray(data.forms)) {
          throw new Error("Invalid data format");
        }
        const formatted = data.forms.map((r) => ({
          _id: r._id,
          id: r._id.slice(-5),
          category: r.inspection_cat?.name || "--",
          date: new Date(r.createdAt).toISOString().split("T")[0],
          type: r.form_cat?.name?.split("-")[1] ?? r.form_cat?.name ?? "--",
          submittedBy: r.user_id?.name || "--",
        }));

        setReportData(formatted);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch reports.");
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  const waitForImagesToLoad = (container) => {
    const imgs = container.querySelectorAll("img");
    return Promise.all(
      Array.from(imgs).map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) resolve();
            else img.onload = img.onerror = resolve;
          })
      )
    );
  };



// Function to get tick/cross symbol for pdfMake
const getAnswerSymbol = (answer) => {
  if (!answer) return "";
  const lower = answer.toLowerCase();
  if (lower === "yes") return { image: tickBase64, width: 10 };
  if (lower === "no") return { image: crossBase64, width: 10 };
  if (lower === "high") return "High"; // optional text
  return answer;
};

// Convert image URL to Base64
const toBase64 = async (url) => {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error("Image fetch failed");
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn("Image load failed:", url, e.message);
    return null;
  }
};

// Main PDF generation function
const generatePdfDoc = async (_id, totalData) => {
  if (!totalData) return;
  const raw = totalData.forms.find((f) => f._id === _id);
  console.log(raw);
  if (!raw) return;

  const content = [
    { text: `${raw.form_cat.name} Inspection Form - ${new Date(raw.createdAt).toLocaleDateString()}`, style: "header", alignment: "center" },
    // { text: `Date: ${new Date(raw.createdAt).toLocaleDateString()}`, alignment: "center", margin: [0, 0, 0, 10] },
  ];

  for (let formIndex = 0; formIndex < raw.form.length; formIndex++) {
    const formSection = raw.form[formIndex];
    content.push({
      text: `${formSection.section || "Untitled Form"}`,
      style: "subheader",
      margin: [0, 5, 0, 5],
    });

    const tableBody = [["Item", "Inspection", "Remarks", "Map", "Images"]];

    for (let item of formSection.items) {
      // Map cell
      const mapCell = item.latlng
        ? {
            text: "Map",
            link: `https://www.google.com/maps?q=${item.latlng}`,
            color: "blue",
            decoration: "underline",
          }
        : "";

      // Images cell
      let imagesCell = "";
      if (item.img && item.img.length) {
        const base64Images = await Promise.all(item.img.map(toBase64));
        const validImages = base64Images.filter(Boolean); // Remove invalid images
        imagesCell = validImages.slice(0, 2).map((b64) => ({ image: b64, width: 120, margin: [0, 2, 0, 2] }));
      }

      tableBody.push([
        item.question || "",
        getAnswerSymbol(item.answer),
        item.remarks || "",
        mapCell,
        imagesCell,
      ]);
    }

    content.push({
      table: { headerRows: 1, widths: ["*", "auto", "*", "auto", "auto"], body: tableBody },
      layout: { fillColor: (rowIndex) => (rowIndex === 0 ? "#CCCCCC" : null) },
      margin: [0, 0, 0, 10],
    });
  }
    
  if (raw.materials?.length) {
    content.push({ text: "Materials Used", style: "subheader", margin: [0, 5, 0, 5] });
    const matBody = [["Material", "Issued", "Used"]];
    raw.materials.forEach((m) => {
      matBody.push([m.material.name, m.issued, m.used]);
    });
    content.push({
      table: { headerRows: 1, widths: ["*", "auto", "auto"], body: matBody },
      layout: { fillColor: (rowIndex) => (rowIndex === 0 ? "#CCCCCC" : null) },
      margin: [0, 0, 0, 10],
    });
  }
  const docDefinition = {
    content,
    styles: {
      header: { fontSize: 18, bold: true },
      subheader: { fontSize: 14, bold: true },
    },
    defaultStyle: { fontSize: 11 },
  };

  pdfMake.createPdf(docDefinition).open();
};


  // const generatePdfDoc = async (_id, totalData) => {
  // const toBase64 = async (url) => {
  //   try {
  //     const res = await fetch(url, { mode: "cors" }); // CORS safe
  //     if (!res.ok) throw new Error("Image fetch failed");
  //     const blob = await res.blob();
  //     return new Promise((resolve, reject) => {
  //       const reader = new FileReader();
  //       reader.onloadend = () => resolve(reader.result);
  //       reader.onerror = reject;
  //       reader.readAsDataURL(blob);
  //     });
  //   } catch (err) {
  //     console.warn("Failed to load image:", url, err);
  //     return null; // fallback
  //   }
  // };

  // const generatePdfDoc = async (_id) => {
  //   if (!totalData) return {};
  //   const raw = totalData.forms.find((f) => f._id === _id);
  //   console.log(raw);
  //   if (!raw) return {};

  //   const doc = new jsPDF("portrait", "mm", "a4");
  //   const W = doc.internal.pageSize.getWidth();
  //   const H = doc.internal.pageSize.getHeight();
  //   const margin = 5;
  //   const bottomMargin = 5;
  //   const lineHeight = 7;
  //   const cellPadding = 3;
  //   const sectionPadding = 0;

  //   const dateStr = new Date(raw.createdAt).toLocaleDateString();
  //   const today = new Date();
  //   const fileDate = today.toISOString().split("T")[0];

  //   const formX = margin;
  //   const formWidth = W - margin * 2;
  //   const formPadding = 5;
  //   const contentX = formX + formPadding + sectionPadding;
  //   const contentWidth = formWidth - 2 * formPadding - 2 * sectionPadding;

  //   // 5 columns: Items / Inspection / Remarks / Location / Image
  //   const colW = [
  //     contentWidth * 0.22, // Items
  //     contentWidth * 0.15, // Inspection
  //     contentWidth * 0.22, // Remarks
  //     contentWidth * 0.2, // Location
  //     contentWidth * 0.21, // Image (width increased)
  //   ];

  //   let y = 40;

  //   const addNewPage = () => {
  //     doc.addPage();
  //     y = margin;
  //     // doc.setFillColor(255, 255, 255);
  //     // doc.rect(0, 0, W, H, "F");
  //   };

  //   // doc.setFillColor(255, 255, 255);
  //   // doc.rect(0, 0, W, H, "F");

  //   doc.setFont("helvetica", "bold").setFontSize(20).setTextColor("#47B291");
  //   doc.text("Mechanical Daily Inspection Form", W / 2, 20, {
  //     align: "center",
  //   });
  //   doc.setFontSize(12).setTextColor("#000");
  //   doc.text(`Date: ${dateStr}`, W / 2, 28, { align: "center" });

  //   // Convert images to base64
  //   const processedItems = await Promise.all(
  //     raw.form[0].items.map(async (it) => ({
  //       ...it,
  //       img:
  //         it.img && it.img.length
  //           ? await Promise.all(it.img.map((url) => toBase64(url)))
  //           : [],
  //     }))
  //   );
  //   console.log("Processed items with base64:", processedItems);

  //   const renderSection = async (title, items, isMaterial = false) => {
  //     let sectionTopY = y;
  //     y += sectionPadding;

  //     const drawHeader = () => {
  //       doc.setFont("helvetica", "bold").setFontSize(12).setTextColor("#000");
  //       doc.text(title, W / 2, (y += 8), { align: "center" });
  //       doc.setDrawColor(220);
  //       doc.line(contentX, (y += 4), contentX + contentWidth, y);
  //       y += 4;

  //       doc.setFontSize(10).setFont("helvetica", "bold");
  //       const headers = isMaterial
  //         ? ["Material", "Issued", "Used"]
  //         : ["Items", "Inspection", "Remarks", "Location", "Image"];

  //       let headerX = contentX;
  //       headers.forEach((h, i) => {
  //         doc.text(h, headerX + colW[i] / 2, y + 4, { align: "center" });
  //         headerX += colW[i];
  //       });
  //       y += 8;
  //       doc.setFont("helvetica", "normal").setFontSize(10);
  //       doc.setDrawColor(200);
  //     };

  //     drawHeader();

  //     const imageHeight = 20;
  //     const imageWidth = 20;

  //     for (const it of items) {
  //       const leftText = doc.splitTextToSize(
  //         isMaterial ? it.material?.name || "" : it.question || "",
  //         colW[0] - 2 * cellPadding
  //       );
  //       const centerText = isMaterial ? it.issued || "" : it.answer || "";
  //       const remarksText = doc.splitTextToSize(
  //         isMaterial ? it.used || "" : it.remarks || "",
  //         colW[2] - 2 * cellPadding
  //       );
  //       const latlng = it.latlng || "";
  //       const googleLink = latlng
  //         ? `https://www.google.com/maps?q=${latlng}`
  //         : "";
  //       const imgArray = Array.isArray(it.img) ? it.img : [];

  //       let lineCount = Math.max(leftText.length, remarksText.length, 1);
  //       let rowHeight = lineCount * lineHeight + 2 * cellPadding;
  //       if (imgArray.length > 0) rowHeight += imageHeight + 4;

  //       if (y + rowHeight + bottomMargin + sectionPadding > H) {
  //         const sectionHeight = y - sectionTopY + sectionPadding;
  //         // doc.roundedRect(
  //         //   formX,
  //         //   sectionTopY,
  //         //   formWidth,
  //         //   sectionHeight,
  //         //   3,
  //         //   3,
  //         //   "D"
  //         // );
  //         addNewPage();
  //         sectionTopY = y;
  //         y += sectionPadding;
  //         drawHeader();
  //       }

  //       let cellX = contentX;

  //       // Items
  //       doc.rect(cellX, y, colW[0], rowHeight);
  //       doc.text(leftText, cellX + colW[0] / 2, y + lineHeight, {
  //         align: "center",
  //       });
  //       cellX += colW[0];

  //       // Inspection
  //       doc.rect(cellX, y, colW[1], rowHeight);
  //       if (!isMaterial && centerText?.toLowerCase() === "yes") {
  //         const tickCenterX = cellX + colW[1] / 2;
  //         const tickCenterY = y + rowHeight / 2;
  //         doc.setDrawColor(0);
  //         doc.setLineWidth(0.5);
  //         doc.line(tickCenterX - 2, tickCenterY, tickCenterX, tickCenterY + 2);
  //         doc.line(
  //           tickCenterX,
  //           tickCenterY + 2,
  //           tickCenterX + 3,
  //           tickCenterY - 2
  //         );
  //         doc.setDrawColor(200);
  //         doc.setLineWidth(0.1);
  //       } else if (centerText) {
  //         doc.text(
  //           centerText.toString(),
  //           cellX + colW[1] / 2,
  //           y + rowHeight / 2 + 2,
  //           { align: "center" }
  //         );
  //       }
  //       cellX += colW[1];

  //       // Remarks
  //       doc.rect(cellX, y, colW[2], rowHeight);
  //       doc.text(remarksText, cellX + colW[2] / 2, y + lineHeight, {
  //         align: "center",
  //       });
  //       cellX += colW[2];

  //       // Location
  //       doc.rect(cellX, y, colW[3], rowHeight);
  //       if (latlng) {
  //         doc.setTextColor(0, 0, 255);
  //         doc.textWithLink(latlng, cellX + colW[3] / 2, y + rowHeight / 2 + 2, {
  //           url: googleLink,
  //           align: "center",
  //         });
  //         doc.setTextColor("#000");
  //       }
  //       cellX += colW[3];
  //       doc.rect(cellX, y, colW[4], rowHeight);

  //       for (let i = 0; i < imgArray.length && i < 2; i++) {
  //         const img = imgArray[i];
  //         if (!img) {
  //           console.warn("Skipping invalid image at index", i);
  //           continue;
  //         }

  //         // Ensure it's base64 string starting with data:image/jpeg;base64,
  //         if (!img.startsWith("data:image")) {
  //           console.warn("Invalid base64 image format", img);
  //           continue;
  //         }

  //         try {
  //           const imgY = y + lineHeight + i * (imageHeight + 2);
  //           const imgX = cellX + (colW[4] - imageWidth) / 2;
  //           const format = img.startsWith("data:image/png") ? "PNG" : "JPEG";
  //           doc.addImage(img, format, imgX, imgY, imageWidth, imageHeight);
  //         } catch (err) {
  //           console.error("Image error:", err);
  //         }
  //       }

  //       y += rowHeight;
  //     }

  //     y += sectionPadding;
  //     const sectionHeight = y - sectionTopY;
  //     // doc.roundedRect(formX, sectionTopY, formWidth, sectionHeight, 3, 3, "D");
  //     y += 10;
  //   };

  //   await renderSection(
  //     `Inspected By: ${raw.user_id.name}` || "Mechanical Report",
  //     processedItems
  //   );

  //   const materialItems = Array.isArray(raw.materials)
  //     ? raw.materials.map((m) => ({
  //         material: m.material,
  //         issued: m.issued,
  //         used: m.used,
  //       }))
  //     : [];

  //   if (materialItems.length > 0) {
  //     if (y + 40 > H) addNewPage();
  //     await renderSection("General Guidelines", materialItems, true);
  //   }

  //   if (y + 20 > H) addNewPage();
  //   doc.setFontSize(10).setTextColor("#999");
  //   // Left-aligned footer
  //   doc.text("Generated by Inspection App Dashboard", margin, H - 10, {
  //     align: "left",
  //   });

  //   // Right-aligned footer
  //   doc.text(`© ${today.getFullYear()}`, W - margin, H - 10, {
  //     align: "right",
  //   });

  //   // 🔹 Open PDF in new tab
  //   doc.output("dataurlnewwindow");

  //   return { doc, fileDate };
  // };

  // Apply filters

  const filteredReports = reportData.filter((r) => {
    const matchesCategory =
      filterCategory === "All" || r.category === filterCategory;
    const matchesType = filterType === "All" || r.type === filterType;
    const matchesSubmittedBy =
      filterSubmittedBy === "All" || r.submittedBy === filterSubmittedBy;
    const matchesDate = !filterDate || r.date === filterDate;

    return matchesCategory && matchesType && matchesSubmittedBy && matchesDate;
  });

  // Recalculate pagination after filtering
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const currentItems = filteredReports.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const uniqueTypes = ["All", ...new Set(reportData.map((r) => r.type))];
  const uniqueCategories = [
    "All",
    ...new Set(reportData.map((r) => r.category)),
  ];
  const uniqueSubmittedBy = [
    "All",
    ...new Set(reportData.map((r) => r.submittedBy)),
  ];

  return (
    <div className="col-12">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title">Reports</h4>
          <ReportsFilters
            filterCategory={filterCategory}
            filterType={filterType}
            filterDate={filterDate}
            filterSubmittedBy={filterSubmittedBy}
            uniqueCategories={uniqueCategories}
            uniqueTypes={uniqueTypes}
            uniqueSubmittedBy={uniqueSubmittedBy}
            setFilterCategory={setFilterCategory}
            setFilterType={setFilterType}
            setFilterDate={setFilterDate}
            setFilterSubmittedBy={setFilterSubmittedBy}
            resetFilters={() => {
              setFilterCategory("All");
              setFilterType("All");
              setFilterDate("");
              setFilterSubmittedBy("All");
            }}
          />
        </div>
        <div className="card-body">
          {loading && <p>Loading...</p>}
          {error && <p className="text-danger">{error}</p>}
          {!loading && !error && (
            <>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Submitted By</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((r) => (
                      <tr key={r._id}>
                        <td>{r.id}</td>
                        <td>{r.category}</td>
                        <td>{r.type}</td>
                        <td>{r.date}</td>
                        <td>{r.submittedBy}</td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="link">
                              •••
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              <Dropdown.Item
                                onClick={async () => {
                                  const pdf = await generatePdfDoc(
                                    r._id,
                                    totalData
                                  );
                                  //if (pdf) pdf.output("dataurlnewwindow");
                                }}
                              >
                                View Report
                              </Dropdown.Item>
                              {/* <Dropdown.Item
                                onClick={async () => {
                                  const { pdf } = await generatePdfDoc(r._id);
                                  if (pdf) pdf.save(`Report-${r.id}.pdf`);
                                }}
                              >
                                Download Report
                              </Dropdown.Item> */}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {currentPage * itemsPerPage + 1} to{" "}
                  {Math.min(
                    (currentPage + 1) * itemsPerPage,
                    filteredReports.length
                  )}{" "}
                  of {filteredReports.length} entries
                </div>
                <div>
                  <button
                    className={`btn btn-sm btn-outline-primary me-2 ${
                      currentPage === 0 ? "disabled" : ""
                    }`}
                    onClick={() =>
                      currentPage > 0 && setCurrentPage(currentPage - 1)
                    }
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      className={`btn btn-sm border me-2 ${
                        currentPage === i ? "btn-primary  text-white" : ""
                      }`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className={`btn btn-sm btn-outline-primary  ${
                      currentPage === totalPages - 1 ? "disabled" : ""
                    }`}
                    onClick={() =>
                      currentPage + 1 < totalPages &&
                      setCurrentPage(currentPage + 1)
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
